import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Shield,
  AlertTriangle,
  Info,
  Activity,
  Clock,
  User as UserIcon,
  RefreshCw,
  Loader2,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const API = window.location.origin;

const SEVERITY_STYLE = {
  info: {
    label: "معلومات",
    icon: Info,
    badge: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    dot: "bg-blue-500",
  },
  warning: {
    label: "تحذير",
    icon: AlertTriangle,
    badge: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    dot: "bg-amber-500",
  },
  critical: {
    label: "حرج",
    icon: Shield,
    badge: "bg-red-500/15 text-red-400 border-red-500/30",
    dot: "bg-red-500",
  },
};

export default function AuditLogPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ info: 0, warning: 0, critical: 0 });
  const [actions, setActions] = useState({});
  const [filters, setFilters] = useState({ severity: "all", action: "all", days: 30 });

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { days: filters.days, limit: 300 };
      if (filters.severity !== "all") params.severity = filters.severity;
      if (filters.action !== "all") params.action = filters.action;
      const res = await axios.get(`${API}/api/audit/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLogs(res.data.logs || []);
      setCounts(res.data.severity_counts || { info: 0, warning: 0, critical: 0 });
      setActions(res.data.available_actions || {});
    } catch {
      toast.error("فشل تحميل السجلات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, token]);

  const formatTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("ar-IQ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const total = counts.info + counts.warning + counts.critical;

  return (
    <div className="space-y-6" dir="rtl" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-emerald-500" />
            سجل العمليات الحساسة
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            تتبّع كل العمليات الحساسة (حذف، تعديل، تبديل ورديات...) للحماية من السرقة والأخطاء
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="refresh-audit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">إجمالي العمليات</p>
              <p className="text-2xl font-bold text-white">{total}</p>
            </div>
            <Activity className="w-8 h-8 text-slate-500" />
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-blue-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">معلومات</p>
              <p className="text-2xl font-bold text-blue-400">{counts.info}</p>
            </div>
            <Info className="w-8 h-8 text-blue-500" />
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-amber-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">تحذيرات</p>
              <p className="text-2xl font-bold text-amber-400">{counts.warning}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-red-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs">عمليات حرجة</p>
              <p className="text-2xl font-bold text-red-400">{counts.critical}</p>
            </div>
            <Shield className="w-8 h-8 text-red-500" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">الفترة</label>
            <Select value={String(filters.days)} onValueChange={(v) => setFilters({ ...filters, days: parseInt(v) })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="filter-days">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">آخر يوم</SelectItem>
                <SelectItem value="7">آخر أسبوع</SelectItem>
                <SelectItem value="30">آخر شهر</SelectItem>
                <SelectItem value="90">آخر 3 أشهر</SelectItem>
                <SelectItem value="365">آخر سنة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">الخطورة</label>
            <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="filter-severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="info">معلومات</SelectItem>
                <SelectItem value="warning">تحذير</SelectItem>
                <SelectItem value="critical">حرج</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">نوع العملية</label>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الأنواع</SelectItem>
                {Object.entries(actions).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label || key}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs list */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">سجلات العمليات ({logs.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <Shield className="w-12 h-12 mx-auto mb-2 text-slate-600" />
              <p>لا توجد عمليات بهذه المعايير</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {logs.map((log) => {
                const sty = SEVERITY_STYLE[log.severity] || SEVERITY_STYLE.info;
                const Icon = sty.icon;
                return (
                  <div key={log.id} className="p-4 hover:bg-slate-800/40 transition-colors" data-testid={`audit-row-${log.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${sty.badge} flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <h4 className="font-bold text-white">{log.action_label || log.action}</h4>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sty.badge}`}>
                            {sty.label}
                          </span>
                        </div>
                        {log.entity_name && (
                          <p className="text-sm text-slate-300 mt-1">
                            <span className="text-slate-500">الكيان: </span>
                            {log.entity_name}
                          </p>
                        )}
                        {log.description && (
                          <p className="text-sm text-slate-400 mt-1">{log.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
                          <span className="flex items-center gap-1">
                            <UserIcon className="w-3 h-3" />
                            {log.actor_name || "—"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(log.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
